import React from 'react';
import { useParams } from 'react-router-dom';
import LocaleLink from '../components/LocaleLink';
import { useContent } from '../hooks/useContent';
import { useScrollToTop } from '../hooks/useScrollToTop';
import SEO from '../components/SEO';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  PageContainer,
  Container,
  PageTitle,
  PageSubtitle,
  ContentText,
  BulletList,
  staggerContainer,
  staggerItem,
  GlassCard,
  StrategicIntelligenceBackLink,
  StrategicIntelligenceBackArrowIcon,
} from '../components/shared/SharedComponents';

const ArticlesSection = styled.section`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
`;

const ArticlesTitle = styled.h3`
  font-size: clamp(22px, 4vw, 26px);
  font-weight: 700;
  color: #1d334e;
  margin: 0 0 24px 0;
  line-height: 1.3;
`;

const ArticlesList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style: none;

  li {
    font-size: 16px;
    color: rgba(29, 51, 78, 0.8);
    margin-bottom: 12px;
    line-height: 1.5;
    position: relative;
    padding-left: 4px;

    &::before {
      content: '';
      position: absolute;
      left: -16px;
      top: 0.55em;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: #BF1922;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ArticleCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin-top: 40px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ArticleCard = styled(GlassCard)`
  padding: 28px;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 50px rgba(15, 23, 42, 0.14);
  }
`;

const ArticleCardLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #BF1922;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const ArticleCardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1d334e;
  margin: 0 0 12px 0;
  line-height: 1.35;
`;

const ArticleCardDescription = styled.p`
  font-size: 15px;
  color: rgba(29, 51, 78, 0.8);
  line-height: 1.6;
  margin: 0 0 20px 0;
  flex: 1;
`;

const ArticleCardButton = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 15px;
  font-weight: 600;
  color: #BF1922;
  transition: transform 0.2s ease, color 0.2s ease;

  ${ArticleCard}:hover & {
    transform: translateX(4px);
    color: #8B1419;
  }
`;

const StrategicIntelligenceCategoryPage = () => {
  useScrollToTop();
  const { getPageContent } = useContent();
  const { categoryId } = useParams();
  const page = getPageContent('strategicIntelligence');
  const category = page?.categories?.[categoryId];

  if (!category) {
    return (
      <PageContainer>
        <Container>
          <PageTitle className="with-underline">
            Category Not Found
          </PageTitle>
          <PageSubtitle>
            The requested category could not be found.
          </PageSubtitle>
          <StrategicIntelligenceBackLink to="/strategic-intelligence">
            <StrategicIntelligenceBackArrowIcon />
            Back to Strategic Intelligence
          </StrategicIntelligenceBackLink>
        </Container>
      </PageContainer>
    );
  }

  const content = category.content || {};
  const bullets = category.bullets || content.bullets || [];
  const bulletsTitle = category.bulletsTitle || content.bulletsTitle;
  const articles = category.articles || [];
  const articlesTitle = category.articlesTitle || 'Recent Publications';
  const articleCards = category.articleCards || [];
  const description = category.description;
  const bulletsIntro = category.bulletsIntro;
  const closing = category.closing;

  return (
    <>
    <SEO
      title={`${category.title} – APEXGATE GLOBAL`}
      description={description || ''}
    />
    <PageContainer>
      <Container>
        <StrategicIntelligenceBackLink to="/strategic-intelligence">
          <StrategicIntelligenceBackArrowIcon />
          {page?.title || 'Strategic Intelligence'}
        </StrategicIntelligenceBackLink>
        <PageTitle className="with-underline">{category.title}</PageTitle>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
            {description && (
              <ContentText as={motion.div} variants={staggerItem}>
                {Array.isArray(description)
                  ? description.map((p, i) => <p key={i}>{p}</p>)
                  : <p>{description}</p>
                }
              </ContentText>
            )}
            {bulletsTitle && bullets.length > 0 && (
              <ContentText as={motion.div} variants={staggerItem}>
                {bulletsIntro && <p>{bulletsIntro}</p>}
                <h3 style={{
                  fontSize: 'clamp(20px, 3vw, 22px)',
                  fontWeight: 700,
                  color: '#1d334e',
                  marginBottom: '16px',
                }}>
                  {bulletsTitle}
                </h3>
                <BulletList>
                  {bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </BulletList>
              </ContentText>
            )}

            {articleCards.length > 0 ? (
              <ArticleCardsGrid as={motion.div} variants={staggerItem}>
                {articleCards.map((card, i) => (
                  <ArticleCard as={LocaleLink} to={card.linkTo} key={i}>
                    <ArticleCardLabel>{card.label}</ArticleCardLabel>
                    <ArticleCardTitle>{card.title}</ArticleCardTitle>
                    {card.description && (
                      <ArticleCardDescription>{card.description}</ArticleCardDescription>
                    )}
                    <ArticleCardButton>
                      {card.buttonText || 'Read Analysis →'}
                    </ArticleCardButton>
                  </ArticleCard>
                ))}
              </ArticleCardsGrid>
            ) : articles.length > 0 && (
              <ArticlesSection as={motion.div} variants={staggerItem}>
                <ArticlesTitle>{articlesTitle}</ArticlesTitle>
                <ArticlesList>
                  {articles.map((article, i) => (
                    <li key={i}>{article}</li>
                  ))}
                </ArticlesList>
              </ArticlesSection>
            )}

            {closing && (
              <ContentText as={motion.div} variants={staggerItem} style={{ marginTop: 48 }}>
                <p>{closing}</p>
              </ContentText>
            )}
        </motion.div>
      </Container>
    </PageContainer>
    </>
  );
};

export default StrategicIntelligenceCategoryPage;
